/**
 * Clinician schedule intent for Loop action-plan items.
 * Persisted under `parameters.clinician_schedule` and mirrored as `schedule_hint`.
 * Recurrence UX mirrors Google Calendar presets, mapped to Hasura weekday flags.
 */

export type ClinicianScheduleMode = "one_time" | "recurring"

/** Matches vira-ui DayTabs. */
export type ClinicianScheduleDay = "Su" | "M" | "Tu" | "W" | "Th" | "F" | "Sa"

export type RecurrencePreset =
  | "none"
  | "daily"
  | "weekly"
  | "weekdays"
  | "custom"

export type RecurrenceEnds =
  | { type: "never" }
  | { type: "on"; date: string }
  | { type: "after"; count: number }

export type ClinicianRecurrence = {
  preset: RecurrencePreset
  interval: number
  unit: "week"
  ends: RecurrenceEnds
}

export type ClinicianSchedule = {
  mode: ClinicianScheduleMode
  days?: ClinicianScheduleDay[]
  /** Start / one-time delivery date (YYYY-MM-DD). */
  date?: string
  /** Local wall-clock HH:mm */
  time: string
  recurrence?: ClinicianRecurrence
  /** How long the client has to respond after delivery (minutes breakdown). */
  expiration?: {
    days: string
    hours: string
    minutes: string
  }
  notifyScope?: "only_me" | "all_practitioners"
}

export const DAY_TO_WEEK: Record<
  ClinicianScheduleDay,
  "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun"
> = {
  Su: "Sun",
  M: "Mon",
  Tu: "Tue",
  W: "Wed",
  Th: "Thu",
  F: "Fri",
  Sa: "Sat",
}

const WEEK_TO_DAY: Record<string, ClinicianScheduleDay> = {
  Sun: "Su",
  Mon: "M",
  Tue: "Tu",
  Wed: "W",
  Thu: "Th",
  Fri: "F",
  Sat: "Sa",
}

const WEEKDAY_HINT: Record<string, ClinicianScheduleDay> = {
  sunday: "Su",
  sun: "Su",
  monday: "M",
  mon: "M",
  tuesday: "Tu",
  tue: "Tu",
  tues: "Tu",
  wednesday: "W",
  wed: "W",
  thursday: "Th",
  thu: "Th",
  thur: "Th",
  thurs: "Th",
  friday: "F",
  fri: "F",
  saturday: "Sa",
  sat: "Sa",
}

export const ALL_DAYS: ClinicianScheduleDay[] = [
  "Su",
  "M",
  "Tu",
  "W",
  "Th",
  "F",
  "Sa",
]
export const WEEKDAY_DAYS: ClinicianScheduleDay[] = ["M", "Tu", "W", "Th", "F"]

const DEFAULT_EXPIRATION = { days: "0", hours: "8", minutes: "0" }
const DEFAULT_ENDS: RecurrenceEnds = { type: "never" }

function pad2(value: number): string {
  return String(value).padStart(2, "0")
}

export function formatDateYmd(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`
}

export function parseYmd(value: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) {
    return null
  }
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  )
  return Number.isNaN(date.getTime()) ? null : date
}

export function weekdayFromYmd(
  dateYmd: string | undefined
): ClinicianScheduleDay {
  if (!dateYmd) {
    return "M"
  }
  const date = parseYmd(dateYmd)
  if (!date) {
    return "M"
  }
  const names: Array<"Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat"> = [
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
  ]
  return WEEK_TO_DAY[names[date.getDay()]]
}

export function longWeekdayName(day: ClinicianScheduleDay): string {
  const map: Record<ClinicianScheduleDay, string> = {
    Su: "Sunday",
    M: "Monday",
    Tu: "Tuesday",
    W: "Wednesday",
    Th: "Thursday",
    F: "Friday",
    Sa: "Saturday",
  }
  return map[day]
}

function defaultRecurrence(preset: RecurrencePreset): ClinicianRecurrence {
  return {
    preset,
    interval: 1,
    unit: "week",
    ends: { ...DEFAULT_ENDS },
  }
}

/** Day after the session (plan start), or tomorrow when session is missing. */
export function planStartDate(
  sessionDate?: Date | null,
  now: Date = new Date()
): Date {
  const base =
    sessionDate && !Number.isNaN(sessionDate.getTime())
      ? new Date(sessionDate)
      : new Date(now)
  const next = new Date(base.getFullYear(), base.getMonth(), base.getDate())
  next.setDate(next.getDate() + 1)
  return next
}

function combineDateAndTime(dateYmd: string, time: string): Date | null {
  const date = parseYmd(dateYmd)
  if (!date) {
    return null
  }
  const match = time.match(/^(\d{1,2}):(\d{2})$/)
  const hour = match ? Number(match[1]) : 10
  const minute = match ? Number(match[2]) : 0
  date.setHours(hour, minute, 0, 0)
  return date
}

/**
 * Roll a one-time schedule forward when its wall-clock time is in the past.
 * Recurring schedules are left unchanged.
 */
export function clampClinicianScheduleToFuture(
  schedule: ClinicianSchedule,
  now: Date = new Date()
): ClinicianSchedule {
  const preset = schedule.recurrence?.preset ?? "none"
  if (preset !== "none") {
    return schedule
  }
  const dateYmd = schedule.date
  if (!dateYmd) {
    return schedule
  }
  const scheduled = combineDateAndTime(dateYmd, schedule.time)
  if (!scheduled || scheduled >= now) {
    return schedule
  }
  // Next calendar day from now, keeping the intended time-of-day.
  const bumped = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1,
    scheduled.getHours(),
    scheduled.getMinutes(),
    0,
    0
  )
  // If that still somehow lands in the past (clock skew), push one more day.
  if (bumped < now) {
    bumped.setDate(bumped.getDate() + 1)
  }
  const date = formatDateYmd(bumped)
  return {
    ...schedule,
    mode: "one_time",
    date,
    days: [weekdayFromYmd(date)],
  }
}

function nextWeekdayOnOrAfter(start: Date, day: ClinicianScheduleDay): Date {
  const cursor = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate()
  )
  for (let offset = 0; offset < 7; offset += 1) {
    const candidate = new Date(cursor)
    candidate.setDate(cursor.getDate() + offset)
    if (ALL_DAYS[candidate.getDay()] === day) {
      return candidate
    }
  }
  return cursor
}

export function defaultClinicianSchedule(
  sessionDate?: Date | null,
  now: Date = new Date()
): ClinicianSchedule {
  const start = planStartDate(sessionDate, now)
  // Never default to a past day when regenerating an old session.
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const next = start < today ? today : start
  const provisional = formatDateYmd(next)
  const schedule: ClinicianSchedule = {
    mode: "one_time",
    date: provisional,
    time: "10:00",
    days: [weekdayFromYmd(provisional)],
    recurrence: defaultRecurrence("none"),
    expiration: { ...DEFAULT_EXPIRATION },
    notifyScope: "only_me",
  }
  return clampClinicianScheduleToFuture(schedule, now)
}

function parseEnds(value: unknown): RecurrenceEnds {
  if (!value || typeof value !== "object") {
    return { type: "never" }
  }
  const record = value as Record<string, unknown>
  if (record.type === "on" && typeof record.date === "string") {
    return { type: "on", date: record.date }
  }
  if (record.type === "after") {
    const count = Number(record.count)
    return {
      type: "after",
      count: Number.isFinite(count) && count > 0 ? Math.floor(count) : 13,
    }
  }
  return { type: "never" }
}

function parseRecurrence(value: unknown): ClinicianRecurrence | undefined {
  if (!value || typeof value !== "object") {
    return undefined
  }
  const record = value as Record<string, unknown>
  const presetRaw = record.preset
  const preset: RecurrencePreset =
    presetRaw === "daily" ||
    presetRaw === "weekly" ||
    presetRaw === "weekdays" ||
    presetRaw === "custom" ||
    presetRaw === "none"
      ? presetRaw
      : "none"
  const interval = Number(record.interval)
  return {
    preset,
    interval:
      Number.isFinite(interval) && interval > 0 ? Math.floor(interval) : 1,
    unit: "week",
    ends: parseEnds(record.ends),
  }
}

export function parseClinicianSchedule(
  value: unknown
): ClinicianSchedule | null {
  if (!value || typeof value !== "object") {
    return null
  }
  const record = value as Record<string, unknown>
  const time =
    typeof record.time === "string" && /^\d{1,2}:\d{2}$/.test(record.time)
      ? record.time.length === 4
        ? `0${record.time}`
        : record.time
      : "10:00"
  const days = Array.isArray(record.days)
    ? record.days.filter((day): day is ClinicianScheduleDay =>
        ALL_DAYS.includes(day as ClinicianScheduleDay)
      )
    : undefined
  const date =
    typeof record.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(record.date)
      ? record.date
      : undefined
  const expiration =
    record.expiration && typeof record.expiration === "object"
      ? {
          days: String(
            (record.expiration as Record<string, unknown>).days ?? "0"
          ),
          hours: String(
            (record.expiration as Record<string, unknown>).hours ?? "8"
          ),
          minutes: String(
            (record.expiration as Record<string, unknown>).minutes ?? "0"
          ),
        }
      : undefined
  const notifyScope =
    record.notifyScope === "all_practitioners" ||
    record.notify_scope === "all_practitioners"
      ? "all_practitioners"
      : record.notifyScope === "only_me" || record.notify_scope === "only_me"
      ? "only_me"
      : undefined

  let recurrence = parseRecurrence(record.recurrence)
  let mode: ClinicianScheduleMode =
    record.mode === "recurring" ? "recurring" : "one_time"

  if (!recurrence) {
    recurrence =
      mode === "recurring"
        ? defaultRecurrence(
            days &&
              days.length === 5 &&
              WEEKDAY_DAYS.every((d) => days.includes(d))
              ? "weekdays"
              : days && days.length === 7
              ? "daily"
              : days && days.length === 1
              ? "weekly"
              : "custom"
          )
        : defaultRecurrence("none")
  }

  mode = recurrence.preset === "none" ? "one_time" : "recurring"

  return {
    mode,
    days,
    date,
    time,
    recurrence,
    expiration,
    notifyScope,
  }
}

/**
 * Apply a recurrence preset and sync mode/days from start date.
 */
export function applyRecurrencePreset(
  schedule: ClinicianSchedule,
  preset: RecurrencePreset,
  customDays?: ClinicianScheduleDay[]
): ClinicianSchedule {
  const startDay = weekdayFromYmd(schedule.date)
  const ends = schedule.recurrence?.ends ?? DEFAULT_ENDS
  if (preset === "none") {
    return {
      ...schedule,
      mode: "one_time",
      days: [startDay],
      recurrence: {
        preset: "none",
        interval: 1,
        unit: "week",
        ends: { type: "never" },
      },
    }
  }
  if (preset === "daily") {
    return {
      ...schedule,
      mode: "recurring",
      days: [...ALL_DAYS],
      recurrence: { preset: "daily", interval: 1, unit: "week", ends },
    }
  }
  if (preset === "weekdays") {
    return {
      ...schedule,
      mode: "recurring",
      days: [...WEEKDAY_DAYS],
      recurrence: { preset: "weekdays", interval: 1, unit: "week", ends },
    }
  }
  if (preset === "weekly") {
    return {
      ...schedule,
      mode: "recurring",
      days: [startDay],
      recurrence: { preset: "weekly", interval: 1, unit: "week", ends },
    }
  }
  const days =
    customDays && customDays.length > 0
      ? customDays
      : schedule.days?.length
      ? schedule.days
      : [startDay]
  return {
    ...schedule,
    mode: "recurring",
    days,
    recurrence: {
      preset: "custom",
      interval: schedule.recurrence?.interval ?? 1,
      unit: "week",
      ends,
    },
  }
}

export function recurrenceSelectOptions(
  startDateYmd: string | undefined
): Array<{
  value: RecurrencePreset
  label: string
}> {
  const day = weekdayFromYmd(startDateYmd)
  const long = longWeekdayName(day)
  return [
    { value: "none", label: "Does not repeat" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: `Weekly on ${long}` },
    { value: "weekdays", label: "Every weekday (Monday to Friday)" },
    { value: "custom", label: "Custom…" },
  ]
}

export function formatRecurrenceSummary(schedule: ClinicianSchedule): string {
  const preset = schedule.recurrence?.preset ?? "none"
  if (preset === "none") {
    return "Does not repeat"
  }
  if (preset === "daily") {
    return "Daily"
  }
  if (preset === "weekdays") {
    return "Every weekday (Monday to Friday)"
  }
  const days = (schedule.days ?? []).map((day) => DAY_TO_WEEK[day]).join(", ")
  if (preset === "weekly") {
    const day = schedule.days?.[0] ?? weekdayFromYmd(schedule.date)
    return `Weekly on ${longWeekdayName(day)}`
  }
  return days ? `Weekly on ${days}` : "Custom"
}

/**
 * Best-effort seed from pipeline `schedule_hint` natural language.
 */
export function seedClinicianScheduleFromHint(
  hint: string | null | undefined,
  sessionDate?: Date | null,
  now: Date = new Date()
): ClinicianSchedule {
  const fallback = defaultClinicianSchedule(sessionDate, now)
  if (!hint || !hint.trim()) {
    return fallback
  }
  const text = hint.trim()
  const lower = text.toLowerCase()

  let time = fallback.time
  const clock24 = text.match(/\b(\d{1,2}):(\d{2})\b/)
  const clock12 = text.match(/\b(\d{1,2})\s*(am|pm)\b/i)
  const isNightly =
    /\bevery\s+night\b/i.test(text) ||
    /\beach\s+night\b/i.test(text) ||
    /\bnightly\b/i.test(text)
  const isEveryEvening = /\bevery\s+evening\b/i.test(text)
  if (clock24) {
    time = `${pad2(Number(clock24[1]))}:${clock24[2]}`
  } else if (clock12) {
    let hour = Number(clock12[1]) % 12
    if (clock12[2].toLowerCase() === "pm") {
      hour += 12
    }
    time = `${pad2(hour)}:00`
  } else if (isNightly || /\bnight\b/i.test(text)) {
    time = "22:00"
  } else if (isEveryEvening || /\bevening\b/i.test(text)) {
    // Keep established "evening" default at 19:00 (including "every evening").
    time = "19:00"
  } else if (/\bmorning\b/i.test(text)) {
    time = "09:00"
  } else if (/\bafternoon\b/i.test(text)) {
    time = "14:00"
  }

  const days = ALL_DAYS.filter((day) => {
    const names = Object.entries(WEEKDAY_HINT)
      .filter(([, d]) => d === day)
      .map(([name]) => name)
    return names.some((name) => new RegExp(`\\b${name}\\b`, "i").test(lower))
  })

  // Daily / nightly wins over a weekday name in the same hint ("every night
  // starting Tuesday" must still be all 7 days, not weekly-on-Tuesday).
  if (
    /\bdaily\b/i.test(text) ||
    /\bevery day\b/i.test(text) ||
    isNightly ||
    isEveryEvening
  ) {
    return applyRecurrencePreset({ ...fallback, time }, "daily")
  }
  if (/\bweekday\b/i.test(text)) {
    return applyRecurrencePreset({ ...fallback, time }, "weekdays")
  }

  const looksRecurring =
    /\b(weekly|every|recurring|each)\b/i.test(text) || days.length > 1

  if (looksRecurring && days.length > 0) {
    const preset =
      days.length === 1
        ? "weekly"
        : days.length === 5 && WEEKDAY_DAYS.every((d) => days.includes(d))
        ? "weekdays"
        : "custom"
    return applyRecurrencePreset({ ...fallback, time, days }, preset, days)
  }

  const isoDate = text.match(/\b(\d{4}-\d{2}-\d{2})\b/)
  if (isoDate) {
    return clampClinicianScheduleToFuture(
      applyRecurrencePreset({ ...fallback, time, date: isoDate[1] }, "none"),
      now
    )
  }

  // Single weekday one-time: land on the next matching day on/after plan start.
  // If that occurrence's time has already passed today, jump a week so we keep
  // the requested weekday (e.g. "Monday at 10:00" on Monday afternoon → next Monday).
  if (days.length === 1) {
    const start = planStartDate(sessionDate, now)
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const floor = start < today ? today : start
    const dated = nextWeekdayOnOrAfter(floor, days[0])
    const dateYmd = formatDateYmd(dated)
    const occurrence = combineDateAndTime(dateYmd, time)
    if (occurrence && occurrence < now) {
      dated.setDate(dated.getDate() + 7)
    }
    const date = formatDateYmd(dated)
    return clampClinicianScheduleToFuture(
      applyRecurrencePreset({ ...fallback, time, date, days }, "none"),
      now
    )
  }

  return clampClinicianScheduleToFuture({ ...fallback, time }, now)
}

function scheduleFromDueDateParam(
  parameters: Record<string, unknown> | undefined,
  sessionDate?: Date | null,
  now: Date = new Date()
): ClinicianSchedule | null {
  if (!parameters) {
    return null
  }
  const dueRaw = parameters.due_date
  if (typeof dueRaw !== "string") {
    return null
  }
  const iso = dueRaw.match(/\b(\d{4}-\d{2}-\d{2})\b/)
  if (!iso) {
    return null
  }
  const promptTime =
    typeof parameters.prompt_time === "string" &&
    /^\d{1,2}:\d{2}$/.test(parameters.prompt_time)
      ? parameters.prompt_time.length === 4
        ? `0${parameters.prompt_time}`
        : parameters.prompt_time
      : "18:00"
  const fallback = defaultClinicianSchedule(sessionDate, now)
  return clampClinicianScheduleToFuture(
    applyRecurrencePreset(
      { ...fallback, time: promptTime, date: iso[1] },
      "none"
    ),
    now
  )
}

export function resolveItemClinicianSchedule(
  item: {
    scheduleHint?: string | null
    parameters?: Record<string, unknown>
  },
  sessionDate?: Date | null,
  now: Date = new Date()
): ClinicianSchedule {
  const fromParams = parseClinicianSchedule(item.parameters?.clinician_schedule)
  if (fromParams) {
    return clampClinicianScheduleToFuture(fromParams, now)
  }
  const fromDue = scheduleFromDueDateParam(item.parameters, sessionDate, now)
  if (fromDue) {
    return fromDue
  }
  return seedClinicianScheduleFromHint(item.scheduleHint, sessionDate, now)
}

export function deriveScheduleHint(schedule: ClinicianSchedule): string {
  const preset = schedule.recurrence?.preset ?? "none"
  if (preset === "none" || schedule.mode === "one_time") {
    return `${schedule.date ?? "tomorrow"} ${schedule.time} one_time`.trim()
  }
  if (preset === "daily") {
    return `daily ${schedule.time} recurring`.trim()
  }
  if (preset === "weekdays") {
    return `weekdays ${schedule.time} recurring`.trim()
  }
  const days = (schedule.days ?? []).join(",")
  return `${days || "weekly"} ${schedule.time} recurring`.trim()
}

function formatTimeLabel(time: string): string {
  const match = time.match(/^(\d{1,2}):(\d{2})$/)
  if (!match) {
    return time
  }
  let hour = Number(match[1])
  const minute = match[2]
  const suffix = hour >= 12 ? "PM" : "AM"
  hour = hour % 12
  if (hour === 0) {
    hour = 12
  }
  return `${hour}:${minute} ${suffix}`
}

function formatEndsSuffix(ends: RecurrenceEnds | undefined): string {
  if (!ends || ends.type === "never") {
    return ""
  }
  if (ends.type === "on") {
    const date = parseYmd(ends.date)
    if (!date) {
      return ""
    }
    return ` · until ${date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`
  }
  return ` · ${ends.count} times`
}

export function formatCadenceLabel(schedule: ClinicianSchedule): string {
  const timeLabel = formatTimeLabel(schedule.time)
  const preset = schedule.recurrence?.preset ?? "none"
  const ends = formatEndsSuffix(schedule.recurrence?.ends)

  if (preset === "none" || schedule.mode === "one_time") {
    if (schedule.date) {
      const date = parseYmd(schedule.date)
      if (date) {
        const weekday = date.toLocaleDateString(undefined, { weekday: "short" })
        const monthDay = date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        })
        return `Does not repeat · ${weekday} ${monthDay} · ${timeLabel}`
      }
    }
    return `Does not repeat · ${timeLabel}`
  }
  if (preset === "daily") {
    return `Daily · ${timeLabel}${ends}`
  }
  if (preset === "weekdays") {
    return `Weekdays · ${timeLabel}${ends}`
  }
  if (preset === "weekly") {
    const day = schedule.days?.[0] ?? weekdayFromYmd(schedule.date)
    return `Weekly on ${DAY_TO_WEEK[day]} · ${timeLabel}${ends}`
  }
  const days = (schedule.days ?? []).map((day) => DAY_TO_WEEK[day]).join(" ")
  return days
    ? `Weekly · ${days} · ${timeLabel}${ends}`
    : `Weekly · ${timeLabel}${ends}`
}

export function theirWeekDaysForSchedule(
  schedule: ClinicianSchedule
): Array<"Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun"> {
  if (schedule.mode === "recurring") {
    return (schedule.days ?? []).map((day) => DAY_TO_WEEK[day])
  }
  if (!schedule.date) {
    return []
  }
  const date = parseYmd(schedule.date)
  if (!date) {
    return []
  }
  const names: Array<"Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat"> = [
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
  ]
  return [names[date.getDay()]]
}

export function serializeClinicianSchedule(
  schedule: ClinicianSchedule
): Record<string, unknown> {
  return {
    mode: schedule.mode,
    days: schedule.days,
    date: schedule.date,
    time: schedule.time,
    recurrence: schedule.recurrence,
    expiration: schedule.expiration,
    notifyScope: schedule.notifyScope ?? "only_me",
  }
}
